import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {InputField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {catch403} from "@pg-mono/request";
import type {IAppError} from "@pg-mono/request-state";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignInHit} from "../../../tracking/algolytics_hits/user_sign_in_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {getFullKMLink} from "../../../utils/get_full_km_link";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignIn} from "../../api/email_sign_in";
import {loginFormDefaultValues} from "../../constants/login_form_default_values";
import {loginFormValidationSchema} from "../../constants/login_form_validation_schema";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {AuthActionType} from "../../types/AuthActionType";
import type {ILogin} from "../../types/ILogin";

interface IProps {
    onSuccessfulSubmit?: () => void;
    userSource: UserSource;
    gtmSourceSection?: string;
}

export const LoginForm = (props: IProps) => {
    const dispatch = useAppDispatch();
    const currentViewType = useAppSelector((state) => state.viewType.current);

    const methods = useFormRHF<ILogin>({defaultValues: loginFormDefaultValues, resolver: yupResolver(loginFormValidationSchema)});

    const onSubmit = (data: ILogin) => {
        emailSignIn(data)
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.LOGIN,
                    eventCategory: GAAuthEventCategory.LOGIN_FORM,
                    source: props.gtmSourceSection ?? null,
                    userSource: props.userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.LOGIN,
                        userSource: props.userSource,
                        gtmSourceSection: props.gtmSourceSection,
                        registrationMethod: "email"
                    })
                );
                userSignInHit(props.userSource, response, currentViewType);

                props.onSuccessfulSubmit?.();
            })
            .catch(
                catch403((error) => {
                    const isErrorWithDetails = (error: Object): error is {detail: string} => {
                        return "detail" in error;
                    };
                    error.responseError &&
                        isErrorWithDetails(error.responseError) &&
                        methods.setError("password", {message: (error.responseError as {detail?: string})?.detail});
                })
            )
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof ILogin, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <form className={form} onSubmit={methods.handleSubmit(onSubmit)}>
                <InputField name="email" placeholder="E-mail" dataTestId={OFFER_TEST_IDS.LOGIN_FORM.FORM_EMAIL_INPUT} />
                <InputField name="password" placeholder="Hasło" type="password" dataTestId={OFFER_TEST_IDS.LOGIN_FORM.FORM_PASSWORD_INPUT} />

                <Link target="_blank" href={getFullKMLink(kmAppLink.site.remindPassword.baseWithParams)}>
                    <Text align="right" variant="info_txt_1" className={css({textDecoration: "underline"})}>
                        Przypomnij hasło
                    </Text>
                </Link>

                <Button className={submit} type="submit" variant="filled_primary" dataTestId={OFFER_TEST_IDS.LOGIN_FORM.LOGIN_BUTTON}>
                    Zaloguj się
                </Button>
            </form>
        </FormProviderRHF>
    );
};

const form = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 2
});

const submit = css({
    width: "100%",
    marginTop: 1
});
